/**
 * planUtils — pure helpers extracted from PlanPage (Cycle 16.C).
 *
 * Pulled out so we can unit-test the routing decision without paying
 * for a full PlanPage render (which entangles useSearchParams, useMe,
 * usePlan, startOfWeekMon, jsdom Date semantics — a maze that
 * deferred Cycle 15.D tests).
 *
 * `pickWorkoutTarget` answers: "athlete tapped a workout in the plan
 * grid — open the detail page, or surface the in-place modal?"
 *
 * Rule: past workouts (already completed/cancelled OR scheduled in
 * the past) → go to detail page immediately. Athlete can edit there.
 * Future workouts (planned/rescheduled today or later) → open the
 * modal so the athlete can pick "start now" / "reschedule" / "skip"
 * без a full page navigation.
 */

import { getDifficulty, type DifficultyLevel } from "../workoutStatus";

export type WorkoutPickInput = {
  /** Workout status from the planner. */
  status: string;
  /** YYYY-MM-DD scheduled date (local). */
  scheduled_date: string;
};

export type WorkoutPickTarget = "detail" | "modal";

const PAST_STATUSES = new Set(["completed", "cancelled"]);

/**
 * Decide whether tapping a workout should navigate to its detail
 * page or open the in-place WorkoutActionModal.
 *
 * @param workout — minimal shape (status + scheduled_date)
 * @param today — current date (defaults to `new Date()`). Pass an
 *                explicit value in tests to keep the decision
 *                deterministic across timezones.
 */
// ── W-REBUILD-2: неделя как нагрузочный профиль ──────────────────

export type LoadItem = {
  scheduled_date: string;
  status: string;
  planned_load?: number | null;
  actual_load?: number | null;
  actual_duration_minutes?: number | null;
  planned_duration_minutes?: number | null;
  actual_distance_km?: number | null;
  planned_distance_km?: number | null;
  discipline?: string | null;
};

/** Нагрузка тренировки для баров/сводки: план, а у выполненных — факт. */
export function itemLoad(w: LoadItem): number | null {
  if (w.status === "completed" || w.status === "done") {
    return w.actual_load ?? w.planned_load ?? null;
  }
  return w.planned_load ?? null;
}

/**
 * Уровень тяжести карточки плана — 2026-08-03.
 *
 * Тот же расчёт, что у тренера в разборе периода: getDifficulty зеркалит
 * shared/services/workout_difficulty.py, а хроническая нагрузка приходит из
 * dashboard.ctl_current — то самое число, которое бэкенд кладёт в разбор
 * (fitness_brief.current). Одна тренировка не может получить у ролей разные
 * ответы, потому что расчёт и вход у них общие.
 *
 * Нагрузка берётся через itemLoad — ровно та, что показана числом TSS на
 * самой карточке. Разойдись они, чип противоречил бы соседней цифре.
 *
 * ОГРАНИЧЕНИЕ ДАННЫХ, НЕ ШКАЛЫ: в ответе плана нет intensity_factor, поэтому
 * без нагрузки расчёт падает сразу на длительность, а у бэкенда между ними
 * есть ещё ветка интенсивности. Совпадение восстановится само, если поле
 * появится в ответе.
 */
export function itemDifficultyLevel(
  w: LoadItem,
  ctlCurrent: number | null | undefined,
): DifficultyLevel | null {
  return (
    getDifficulty(
      {
        planned_load: itemLoad(w),
        planned_duration_minutes:
          (w.status === "completed" || w.status === "done"
            ? w.actual_duration_minutes
            : null) ?? w.planned_duration_minutes ?? null,
      },
      { ctl_current: ctlCurrent ?? null },
    )?.level ?? null
  );
}

export interface WeekTotals {
  tss: number;
  hours: number;
  km: number;
  disciplines: number;
  /** Сколько тренировок вошло в часы/км, но не в TSS (нагрузка неизвестна). */
  itemsWithoutLoad: number;
}

/** Итоги недели. Чинит баг «TSS · общий 0»: считаем и факт, и план. */
export function weekTotals(items: ReadonlyArray<LoadItem>): WeekTotals {
  let tss = 0;
  let minutes = 0;
  let km = 0;
  let noLoad = 0;
  const sports = new Set<string>();
  for (const w of items) {
    const load = itemLoad(w);
    const mins = Number(w.actual_duration_minutes ?? w.planned_duration_minutes ?? 0);
    const dist = Number(w.actual_distance_km ?? w.planned_distance_km ?? 0);
    // Тренировка без нагрузки, но с длительностью или дистанцией, попадает
    // в часы и километры и НЕ попадает в TSS. У импортных планов load пуст,
    // и строка итогов выходила из разного: «59 TSS» по одним тренировкам,
    // «9.8 ч · 27 км» по другим — три числа с разными основаниями подряд.
    // Молча складывать их нельзя, поэтому считаем и называем разницу.
    if (load == null && (mins > 0 || dist > 0)) noLoad += 1;
    tss += load ?? 0;
    minutes += mins;
    km += dist;
    if (w.discipline) sports.add(String(w.discipline).toLowerCase());
  }
  return {
    tss: Math.round(tss),
    hours: minutes / 60,
    km,
    disciplines: sports.size,
    itemsWithoutLoad: noLoad,
  };
}

/**
 * Суммы TSS по неделям для спарклайна: items за N недель, mondayISO —
 * понедельник ПЕРВОЙ недели окна. Возвращает массив длины weeks
 * (старые → новые).
 */
export function weeklyLoadSums(
  items: ReadonlyArray<LoadItem>,
  firstMondayISO: string,
  weeks: number,
): number[] {
  const [y, m, d] = firstMondayISO.split("-").map(Number);
  const start = Date.UTC(y, m - 1, d);
  const sums = new Array(weeks).fill(0);
  for (const w of items) {
    const [wy, wm, wd] = w.scheduled_date.split("-").map(Number);
    const t = Date.UTC(wy, wm - 1, wd);
    const idx = Math.floor((t - start) / (7 * 86_400_000));
    if (idx >= 0 && idx < weeks) sums[idx] += itemLoad(w) ?? 0;
  }
  return sums.map((v) => Math.round(v));
}

export type WeekDeltaWord = "harder" | "easier" | "same" | "recovery" | null;

/**
 * Дельта текущей недели к прошлой: проценты + слово-итог.
 * null pct — прошлая неделя пустая (сравнивать не с чем).
 */
export function weekDelta(
  currentTss: number,
  prevTss: number,
  /**
   * У всех тренировок текущей недели известна нагрузка. Когда нет — сравнивать
   * не с чем: сумма занижена не планом, а пробелом в данных.
   */
  currentIsComplete = true,
): { pct: number | null; word: WeekDeltaWord } {
  if (prevTss <= 0) return { pct: null, word: null };
  // Инцидент 2026-08-10. Сторож от деления на ноль стоял только на знаменателе.
  // На проде у шести из семи тренировок недели не был проставлен TSS, сумма
  // вышла 0, дельта — -100%, и неделя с трёхчасовым длинным вело и бриком
  // подписалась «разгрузочная». Оговорку «TSS без 6 тренировок» экран показывал
  // рядом, но вывод её не наследовал — читалось как факт о плане.
  if (!currentIsComplete) return { pct: null, word: null };
  const pct = Math.round(((currentTss - prevTss) / prevTss) * 100);
  let word: WeekDeltaWord = "same";
  if (pct <= -30) word = "recovery";
  else if (pct <= -10) word = "easier";
  else if (pct >= 10) word = "harder";
  return { pct, word };
}

export function pickWorkoutTarget(
  workout: WorkoutPickInput,
  today: Date = new Date(),
): WorkoutPickTarget {
  // Explicit past statuses always route to detail — athlete may want
  // to add RPE/feel/note even for a workout they already cancelled.
  if (PAST_STATUSES.has(workout.status)) return "detail";

  // Otherwise compare dates at midnight (local). A planned workout
  // older than today is treated as past: athlete probably forgot to
  // mark it, surfacing the modal would be confusing.
  const todayMidnight = new Date(today);
  todayMidnight.setHours(0, 0, 0, 0);
  const wDate = new Date(workout.scheduled_date + "T00:00:00");
  if (wDate < todayMidnight) return "detail";

  return "modal";
}
